import React, { useState } from 'react';
import { Box, Paper, Typography, Grid, TextField, Button, CircularProgress, Alert, Divider } from '@mui/material';
import Header from '../components/Header';
import { transferPalantirData, copyPalantirDataset, ingestClientDocs, deleteFolderInfo, transferData } from '../services/apiClient';

export default function DataIngestionPage({ onLogout }) {
    // State for Palantir-BQ Transfer form
    const [formData, setFormData] = useState({ datasetRid: '', tableName: '', rowCount: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });

    // State for Copy Data action
    const [isCopying, setIsCopying] = useState(false);
    const [copyStatus, setCopyStatus] = useState({ type: '', text: '' });

    // State for Ingest Docs form
    const [clientName, setClientName] = useState('');
    const [isIngesting, setIsIngesting] = useState(false);
    const [ingestStatus, setIngestStatus] = useState({ type: '', text: '' });

    // State for Delete Folder form
    const [folderName, setFolderName] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteStatus, setDeleteStatus] = useState({ type: '', text: '' });

    // State for Create Files action
    const [isTransferring, setIsTransferring] = useState(false);
    const [transferStatus, setTransferStatus] = useState({ type: '', text: '' });

    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setFormData(prevState => ({ ...prevState, [name]: value }));
    };

    const handlePalantirSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        setStatusMessage({ type: '', text: '' });
        try {
            await transferPalantirData(formData);
            setStatusMessage({ type: 'success', text: 'Data transfer initiated successfully!' });
            setFormData({ datasetRid: '', tableName: '', rowCount: '' });
        } catch (error) {
            setStatusMessage({ type: 'error', text: `Error: ${error.message}` });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopy = async () => {
        setIsCopying(true);
        setCopyStatus({ type: '', text: '' });
        try {
            await copyPalantirDataset();
            setCopyStatus({ type: 'success', text: 'Copy dataset process initiated successfully!' });
        } catch (error) {
            setCopyStatus({ type: 'error', text: `Error: ${error.message}` });
        } finally {
            setIsCopying(false);
        }
    };

    const handleIngestDocsSubmit = async (event) => {
        event.preventDefault();
        setIsIngesting(true);
        setIngestStatus({ type: '', text: '' });
        try {
            await ingestClientDocs(clientName);
            setIngestStatus({ type: 'success', text: `Ingestion started successfully for client: ${clientName}` });
            setClientName('');
        } catch (error) {
            setIngestStatus({ type: 'error', text: `Error: ${error.message}` });
        } finally {
            setIsIngesting(false);
        }
    };

    const handleDeleteFolderSubmit = async (event) => {
        event.preventDefault();
        if (!window.confirm(`Are you sure you want to delete all files in the folder "${folderName}"? This action cannot be undone.`)) {
            return;
        }
        setIsDeleting(true);
        setDeleteStatus({ type: '', text: '' });
        try {
            const response = await deleteFolderInfo(folderName);
            setDeleteStatus({ type: 'success', text: `${response.message} (${response.deleted_files_count} files deleted).` });
            setFolderName('');
        } catch (error) {
            setDeleteStatus({ type: 'error', text: `Error: ${error.message}` });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleTransferData = async () => {
        setIsTransferring(true);
        setTransferStatus({ type: '', text: '' });
        try {
            const response = await transferData();
            setTransferStatus({ type: 'success', text: response.message || 'Data transfer process started successfully!' });
        } catch (error) {
            setTransferStatus({ type: 'error', text: `Error: ${error.message}` });
        } finally {
            setIsTransferring(false);
        }
    };

    return (
        <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            <Header onLogout={onLogout} />

            {/* Section 1: Palantir to BQ */}
            <Paper sx={{ p: 3, mt: 3, maxWidth: '800px', mx: 'auto' }}>
                <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Palantir to BigQuery Data Ingestion
                </Typography>
                <form onSubmit={handlePalantirSubmit}>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField required fullWidth name="datasetRid" label="Palantir Dataset RID" value={formData.datasetRid} onChange={handleFormChange} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField required fullWidth name="tableName" label="BigQuery Table Name" value={formData.tableName} onChange={handleFormChange} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth name="rowCount" label="Number of Rows (Optional)" type="number" value={formData.rowCount} onChange={handleFormChange} />
                        </Grid>
                        <Grid item xs={12} sx={{ mt: 2, display: 'flex', gap: 2 }}>
                            <Button type="submit" variant="contained" disabled={isLoading} startIcon={isLoading ? <CircularProgress size={20} /> : null}>
                                {isLoading ? 'Submitting...' : 'Initiate Transfer'}
                            </Button>
                            <Button onClick={handleCopy} variant="outlined" disabled={isCopying} startIcon={isCopying ? <CircularProgress size={20} /> : null}>
                                {isCopying ? 'Copying...' : 'Copy Data'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
                {statusMessage.text && <Alert severity={statusMessage.type} sx={{ mt: 3 }}>{statusMessage.text}</Alert>}
                {copyStatus.text && <Alert severity={copyStatus.type} sx={{ mt: 3 }}>{copyStatus.text}</Alert>}
            </Paper>

            <Divider sx={{ my: 4, maxWidth: '800px', mx: 'auto' }} />

            {/* Section 2: Ingest Client Docs */}
            <Paper sx={{ p: 3, maxWidth: '800px', mx: 'auto' }}>
                <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Ingest Client Documents
                </Typography>
                <form onSubmit={handleIngestDocsSubmit}>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField required fullWidth name="clientName" label="Client Name" value={clientName} onChange={(e) => setClientName(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Button type="submit" variant="contained" color="secondary" disabled={isIngesting || !clientName} startIcon={isIngesting ? <CircularProgress size={20} /> : null}>
                                {isIngesting ? 'Ingesting...' : 'Ingest Docs'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
                {ingestStatus.text && <Alert severity={ingestStatus.type} sx={{ mt: 3 }}>{ingestStatus.text}</Alert>}
            </Paper>

            <Divider sx={{ my: 4, maxWidth: '800px', mx: 'auto' }} />

            {/* Section 3: Delete Folder */}
            <Paper sx={{ p: 3, maxWidth: '800px', mx: 'auto' }}>
                <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    Delete Folder Contents
                </Typography>
                <form onSubmit={handleDeleteFolderSubmit}>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField required fullWidth name="folderName" label="Folder Name" value={folderName} onChange={(e) => setFolderName(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Button type="submit" variant="contained" color="error" disabled={isDeleting || !folderName} startIcon={isDeleting ? <CircularProgress size={20} /> : null}>
                                {isDeleting ? 'Deleting...' : 'Delete Files'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
                {deleteStatus.text && <Alert severity={deleteStatus.type} sx={{ mt: 3 }}>{deleteStatus.text}</Alert>}
            </Paper>

            <Divider sx={{ my: 4, maxWidth: '800px', mx: 'auto' }} />

            {/* Section 4: Create Files */}
            <Paper sx={{ p: 3, maxWidth: '800px', mx: 'auto' }}>
                <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Create Files from Transferred Data
                </Typography>
                <Box>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleTransferData}
                        disabled={isTransferring}
                        startIcon={isTransferring ? <CircularProgress size={20} /> : null}
                    >
                        {isTransferring ? 'Processing...' : 'Create Files'}
                    </Button>
                </Box>
                {transferStatus.text && <Alert severity={transferStatus.type} sx={{ mt: 3 }}>{transferStatus.text}</Alert>}
            </Paper>

        </Box>
    );
};

